import { Formik, FormikProps } from "formik";
import Input from "../shared/Input/Input";
import Button from "@mui/material/Button";
import "./Register.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import { errorMessages } from "../../constants/errorMessages";
import { regex } from "../../constants/regex";
import { UserService } from "../../services/UserService";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetDetailsBySubscriptionIdQuery,
  useLazyRegisterSetPasswordQuery,
} from "../../services/rtkQueryServices/userService";
import { LOGIN_LITERALS } from "../../constants/literals";
import { useAuth } from "../../context/AuthProvider";
import { useLazySignOutQuery } from "../../services/rtkQueryServices/authenticationService";
import { routePaths } from "../../constants/routePaths";
import { dataStore } from "../../constants/dataStore";

const Register = (props: any) => {
  const [searchParams] = useSearchParams();
  const subscriptionId = searchParams.get("sub_id");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const userDetails = useGetDetailsBySubscriptionIdQuery(subscriptionId);
  const [setPassword] = useLazyRegisterSetPasswordQuery();
  const [setSignOut] = useLazySignOutQuery();

  const auth = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (
      UserService.isLoggedIn === "true" &&
      subscriptionId !== UserService.subscriptionId
    ) {
      setSignOut({}).then((res) => {
        localStorage.removeItem(dataStore.subscriptionId);
        auth?.logOut(false);
      });
    } else if (
      UserService.isLoggedIn === "true" &&
      subscriptionId === UserService.subscriptionId
    ) {
      auth?.logIn();
    }
  }, []);

  const initialFormValue = useMemo<any>(() => {
    if (userDetails.isSuccess) {
      if (userDetails.data?.data?.isUserExists) {
        navigate(routePaths.login, {
          state: { isAccountExists: userDetails.data?.data?.isUserExists , matchType: userDetails.data?.data?.registeredUserMismatchErrorType},
        });
      } else if (userDetails.data?.data?.clinicUserDetails) {
        return {
          emailAddress: userDetails.data?.data?.clinicUserDetails?.userEmail,
          firstName: userDetails.data?.data?.clinicUserDetails?.firstName,
          lastName: userDetails.data?.data?.clinicUserDetails?.lastName,
          password: "",
          confirmPassword: "",
        };
      }
    } else if (userDetails.isError) {
      setErrorMessage((userDetails?.error as any)?.data?.message);
      return {
        emailAddress: "",
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
        emailOtp: "",
      };
    }
  }, [userDetails]);
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(errorMessages.required("First Name")),

    lastName: Yup.string().required(errorMessages.required("Last Name")),

    emailAddress: Yup.string()
      .required(errorMessages.required("Email address"))
      .matches(regex.emailValidation, errorMessages.invalidEmail)
      .max(128, errorMessages.characterLimit("Email address", 128)),
    password: Yup.string()
      .required(errorMessages.required("Password"))
      .min(8, errorMessages.minCharacterLimit("Password", 8))
      .max(64, errorMessages.characterLimit("Password", 64))
      .matches(regex.passwordValidation, errorMessages.passwordRegexMatch),
    confirmPassword: Yup.string()
      .required(errorMessages.required("Password"))
      .oneOf([Yup.ref("password")], errorMessages.confirmPassword),
  });
  const formRef = useRef<FormikProps<any>>(null);
  const handleSubmit = (values: any) => {
    setPassword({
      password: values?.password,
      confirmPassword: values?.confirmPassword,
    }).then((res) => {
      if (res.isSuccess) {
        UserService.currentStep = "2";
        props.nextStep();
      } else if (res.isError) {
        setErrorMessage(
          (res.error as any)?.data?.message || LOGIN_LITERALS.somethingWentWrong
        );
      }
    });
  };

  return (
    <div className="register-form-container h-100 pt-4 d-flex flex-column">
      <div className="register-header align-self-center pt-5">
        Set up your account
      </div>
      <Formik
        initialValues={initialFormValue}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values: any) => {
          handleSubmit(values);
        }}
        innerRef={formRef}
      >
        {(formik) => (
          <form
            //   onKeyDown={Utility.preventSubmitOnEnter}
            onSubmit={formik.handleSubmit}
            className="d-flex flex-column registration-form"
            autoComplete="off"
          >
            <div className="register-clinic pb-3">
              <div className="user-details">
                <Input
                  name="firstName"
                  placeholder="First Name"
                  onChange={formik.handleChange}
                  value={formik?.values?.firstName}
                  errorMsg={
                    formik.touched.firstName &&
                    formik.errors.firstName &&
                    (formik.errors as any).firstName
                  }
                  disabled
                  onInput={formik.handleChange}
                  maxLength={64}
                  label="First Name"
                  required
                />
                <Input
                  name="lastName"
                  placeholder="Last Name"
                  onChange={formik.handleChange}
                  value={formik?.values?.lastName}
                  disabled
                  errorMsg={
                    formik.touched.lastName &&
                    formik.errors.lastName &&
                    (formik.errors as any).lastName
                  }
                  maxLength={64}
                  label="Last Name"
                  required
                />
                <Input
                  name={"emailAddress"}
                  placeholder="Email Address"
                  disabled
                  onChange={(e) => {
                    formik.handleChange(e);
                  }}
                  value={formik?.values?.emailAddress}
                  autoComplete="off"
                  maxLength={128}
                  errorMsg={
                    formik.touched.emailAddress &&
                    formik.errors.emailAddress &&
                    (formik.errors as any).emailAddress
                  }
                  onInput={formik.handleChange}
                  required
                  label="Email address"
                />
                <Input
                  name="password"
                  label="Password"
                  placeholder="Enter Password"
                  type="password"
                  showHelpTextIcon
                  labelHelpText={errorMessages.passwordRegexMatch}
                  autoComplete="off"
                  infoIconProps={{
                    width: "12px",
                    height: "12px",
                    fillColor: "#636363",
                  }}
                  errorMsg={
                    formik.touched.password &&
                    formik.errors.password &&
                    (formik.errors as any).password
                  }
                  value={formik?.values?.password}
                  onInput={(e) => {
                    formik.handleChange(e);
                    setErrorMessage("");
                  }}
                  required
                />
                <Input
                  name="confirmPassword"
                  label="Confirm Password"
                  placeholder="Confirm your Password"
                  type="password"
                  errorMsg={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword &&
                    (formik.errors as any).confirmPassword
                  }
                  value={formik?.values?.confirmPassword}
                  onInput={(e) => {
                    formik.handleChange(e);
                    setErrorMessage("");
                  }}
                  required
                />
              </div>
            </div>
            <div className="d-flex flex-column justify-content-between gap-3 py-3 registration-form-button">
              <div className="error-message text-align-center">
                {errorMessage}
              </div>
              <Button
                variant="contained"
                type="submit"
                disabled={!formik.isValid || !formik.dirty}
              >
                Next
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
export default Register;
